import { ethers, parseUnits } from "ethers"
import { create } from "zustand"

import { persist, createJSONStorage } from "zustand/middleware"

import { STEPS } from "modules/etherlink/config"
import { useHistory } from "react-router-dom"

import StandardFactoryAbi from "assets/abis/StandardFactoryNonTransferable.json"
import StandardFactoryTAbi from "assets/abis/StandardFactoryTransferable.json"
import StandardFactoryWAbi from "assets/abis/StandardFactoryWrapped.json"

import { useCallback, useContext, useState } from "react"
import { useTezos } from "services/beacon/hooks/useTezos"
import { EtherlinkContext } from "services/wagmi/context"
import { useNotification } from "modules/common/hooks/useNotification"

interface EvmDaoCreateStore {
  currentStep: number
  data: Record<string, any>
  loading: Record<string, boolean>
  errors: Record<string, any | null>
  touched: Record<string, any>
  getIn: (field: string) => any
  setFieldValue: (field: string, value: any) => void
  next: {
    text: string
    handler: () => string | undefined
  }
  prev: {
    text: string
    handler: () => string | undefined
  }
}

const useEvmDaoCreateZustantStore = create<EvmDaoCreateStore>()(
  persist(
    (set, get) => ({
      currentStep: 0,
      data: {
        name: "",
        template: "full",
        symbol: "",
        description: "",
        administrator: "",
        tokenDeploymentMechanism: "new",
        wrappedTokenSymbol: "",
        wrappedTokenName: "",
        underlyingTokenAddress: "",
        governanceToken: {
          address: "",
          symbol: "",
          tokenDecimals: 18
        },
        quorum: {
          returnedTokenPercentage: 4,
          proposalThresholdPercentage: 0,
          proposalThreshold: 0
        },
        members: [{ address: "", amountOfTokens: 0, error: "" }],
        voting: {
          votingBlocksDay: 0,
          votingBlocksHours: 0,
          votingBlocksMinutes: 0,
          proposalFlushBlocksDay: 0,
          proposalFlushBlocksHours: 0,
          proposalFlushBlocksMinutes: 0,
          proposalExpiryBlocksDay: 0,
          proposalExpiryBlocksHours: 0,
          proposalExpiryBlocksMinutes: 0
        },
        registry: {}
      },
      getIn: (field: string) => {
        if (field.includes(".")) {
          const [parent, child] = field.split(".")
          return get().data[parent][child]
        }
        return get().data[field]
      },
      setFieldValue: (field: string, value: any) => {
        if (field.includes(".")) {
          const [parent, child] = field.split(".")
          set({
            data: {
              ...get().data,
              [parent]: {
                ...(get().data[parent] || {}),
                [child]: value
              }
            },
            touched: {
              ...get().touched,
              [parent]: {
                ...(get().touched[parent] || {}),
                [child]: true
              }
            }
          })
        } else {
          set({
            data: { ...get().data, [field]: value },
            touched: { ...get().touched, [field]: true }
          })
        }
      },
      next: {
        text: "Next",
        handler: () => {
          const { currentStep } = get()
          if (currentStep === STEPS.length - 1) return
          console.log("Moving to Next Step", currentStep)
          set({ currentStep: currentStep + 1 })
          return STEPS[currentStep + 1]?.path
        }
      },
      prev: {
        text: "Back",
        handler: () => {
          const { currentStep } = get()
          if (currentStep === 0) return
          console.log("Moving to Previous Step", currentStep)
          set({ currentStep: currentStep - 1 })
          return STEPS[currentStep - 1]?.path
        }
      },
      loading: {},
      touched: {
        name: false,
        symbol: false,
        description: false,
        administrator: false,
        governanceToken: {
          address: false,
          symbol: false,
          tokenDecimals: false
        }
      },
      errors: {
        name: "",
        symbol: "",
        description: "",
        administrator: "",
        governanceToken: {
          address: "",
          symbol: "",
          tokenDecimals: 0
        }
      }
    }),
    {
      name: "evm-dao-create-store",
      partialize: state => ({
        data: state.data,
        touched: state.touched,
        errors: state.errors
      }),
      storage: createJSONStorage(() => localStorage)
    }
  )
)

const useEvmDaoCreateStore = () => {
  const [isDeploying, setIsDeploying] = useState(false)
  const data = useEvmDaoCreateZustantStore()
  const history = useHistory()
  const { contractData } = useContext<{
    contractData: {
      wrapper: string | undefined
      wrapper_t: string | undefined
      wrapper_w: string | undefined
    }
  }>(EtherlinkContext)

  const { etherlink, network } = useTezos()
  const notify = useNotification()

  const deployDaoWithWrapper = useCallback(async () => {
    const daoData = data.data
    console.log("=== Starting DAO Deployment ===")
    console.log("Full DAO Data:", daoData)

    let selectedWrapperAddress: string | undefined
    if (daoData.tokenDeploymentMechanism === "wrapped") {
      selectedWrapperAddress = contractData?.wrapper_w
    } else if (daoData.nonTransferable) {
      selectedWrapperAddress = contractData?.wrapper
    } else {
      selectedWrapperAddress = contractData?.wrapper_t
    }

    try {
      const proposalThreshold = daoData.quorum.proposalThreshold || daoData.quorum.proposalThresholdPercentage || 0
      const quorumThreshold = daoData.quorum.returnedTokenPercentage

      // Convert execution delay values to numbers
      const executionDelayDays = Number(daoData.voting.proposalExpiryBlocksDay) || 0
      const executionDelayHours = Number(daoData.voting.proposalExpiryBlocksHours) || 0
      const executionDelayMinutes = Number(daoData.voting.proposalExpiryBlocksMinutes) || 0

      const executationDelayinSeconds =
        executionDelayDays * 24 * 60 * 60 + executionDelayHours * 60 * 60 + executionDelayMinutes * 60

      // Convert all values to numbers to avoid string concatenation
      const votingDelayDays = Number(daoData.voting.votingBlocksDay) || 0
      const votingDelayHours = Number(daoData.voting.votingBlocksHours) || 0
      const votingDelayMinutes = Number(daoData.voting.votingBlocksMinutes) || 0

      const votingDelayInMinutes = votingDelayDays * 24 * 60 + votingDelayHours * 60 + votingDelayMinutes

      // Convert voting duration values
      const votingDurationDays = Number(daoData.voting.proposalFlushBlocksDay) || 0
      const votingDurationHours = Number(daoData.voting.proposalFlushBlocksHours) || 0
      const votingDurationMinutes = Number(daoData.voting.proposalFlushBlocksMinutes) || 0

      const votingDurationInMinutes = votingDurationDays * 24 * 60 + votingDurationHours * 60 + votingDurationMinutes
      console.log("Token deployment mechanism:", daoData.tokenDeploymentMechanism)
      console.log("Signer:", etherlink.signer)

      if (!selectedWrapperAddress) {
        const factoryType =
          daoData.tokenDeploymentMechanism === "wrapped"
            ? "wrapped ERC20"
            : daoData.nonTransferable
              ? "non-transferable"
              : "transferable"

        console.error("No wrapper address found!", {
          factoryType,
          tokenDeploymentMechanism: daoData.tokenDeploymentMechanism,
          nonTransferable: daoData.nonTransferable
        })
        throw new Error(
          `Wrapper contract address not found for ${factoryType} token DAO. Please check your network configuration.`
        )
      }

      // Validate signer
      if (!etherlink.signer) {
        console.error("No signer available!", etherlink)
        throw new Error("Wallet not connected. Please connect your wallet.")
      }

      // Select ABI based on flow; fall back to StandardFactoryAbi when a specific variant isn't available
      let selectedAbi: any
      if (daoData.tokenDeploymentMechanism === "wrapped") {
        selectedAbi = (StandardFactoryWAbi as any)?.abi
      } else if (daoData.nonTransferable) {
        selectedAbi = (StandardFactoryAbi as any)?.abi
      } else {
        selectedAbi = (StandardFactoryTAbi as any)?.abi
      }

      // Preflight: verify contract code exists at address
      const onChainCode = await etherlink.provider.getCode(selectedWrapperAddress)
      if (!onChainCode || onChainCode === "0x") {
        throw new Error(
          `No contract code at ${selectedWrapperAddress}. Check network and wrapper address configuration.`
        )
      }

      const wrapperFactory: ethers.Contract = new ethers.Contract(selectedWrapperAddress, selectedAbi, etherlink.signer)

      console.log("Wrapper factory created:", {
        address: wrapperFactory.address || wrapperFactory.target,
        hasDeployDAOwithToken: typeof (wrapperFactory as any).deployDAOwithToken === "function",
        hasDeployDAOwithWrappedToken: typeof (wrapperFactory as any).deployDAOwithWrappedToken === "function"
      })

      // Validate timing values before deployment
      if (isNaN(votingDelayInMinutes) || votingDelayInMinutes < 0) {
        console.error("Invalid voting delay:", votingDelayInMinutes)
        throw new Error("Invalid voting delay value. Please check your input.")
      }

      if (isNaN(votingDurationInMinutes) || votingDurationInMinutes < 0) {
        console.error("Invalid voting duration:", votingDurationInMinutes)
        throw new Error("Invalid voting duration value. Please check your input.")
      }

      if (isNaN(executationDelayinSeconds) || executationDelayinSeconds < 0) {
        console.error("Invalid execution delay:", executationDelayinSeconds)
        throw new Error("Invalid execution delay value. Please check your input.")
      }

      setIsDeploying(true)

      let wrapper: any // This will be a TransactionResponse

      if (daoData.tokenDeploymentMechanism === "wrapped") {
        // Deploy with wrapped token using the StandardFactoryWrapped tuple structure
        const registryKeys = Object.keys(daoData.registry || {})
        const registryValues = Object.values(daoData.registry || {}).map(v => String(v))

        const governanceSettings = [
          BigInt(Math.min(Math.max(votingDelayInMinutes, 0), 2 ** 48 - 1)), // voting delay (minutes)
          BigInt(Math.min(Math.max(votingDurationInMinutes, 0), 2 ** 32 - 1)), // voting period (minutes)
          BigInt(proposalThreshold || 0), // proposal threshold
          BigInt(Math.min(Math.max(Number(quorumThreshold), 0), 100)) // quorum fraction percentage
        ]

        const wrappedDaoPayload = {
          name: daoData.name || "",
          symbol: daoData.wrappedTokenSymbol || "",
          description: daoData.description || "",
          executionDelay: BigInt(Math.max(Math.floor(executationDelayinSeconds), 0)),
          underlyingTokenAddress: daoData.underlyingTokenAddress,
          governanceSettings,
          keys: registryKeys,
          values: registryValues,
          transferrableStr: String(!daoData.nonTransferable).toLowerCase()
        }

        console.log(
          "Deploying wrapped token DAO with payload:",
          JSON.stringify(wrappedDaoPayload, (key, value) => (typeof value === "bigint" ? value.toString() : value), 2)
        )

        try {
          wrapper = await wrapperFactory.deployDAOwithWrappedToken(wrappedDaoPayload)
        } catch (contractError) {
          console.error("Contract call error:", contractError)
          console.error("Error details:", {
            message: (contractError as any).message,
            code: (contractError as any).code,
            data: (contractError as any).data
          })
          throw contractError
        }
      } else {
        // Deploy with new token
        const totalTokenSupply = daoData.members.reduce((acc: number, member: any) => acc + member.amountOfTokens, 0)

        // First, create the member amounts array
        const memberAmounts = daoData.members.map((member: any) => {
          try {
            const amount = parseUnits(member.amountOfTokens.toString(), daoData.governanceToken.tokenDecimals)
            console.log(`Member ${member.address} amount: ${member.amountOfTokens} -> ${amount.toString()}`)
            return amount.toString()
          } catch (e) {
            console.error(`Error parsing amount for member ${member.address}:`, e)
            return "0"
          }
        })

        // Append the 4 DAO settings to the initialAmounts array
        const initialAmountsWithSettings = [
          ...memberAmounts,
          votingDelayInMinutes.toString(), // DAO setting 1: voting delay
          votingDurationInMinutes.toString(), // DAO setting 2: voting duration
          proposalThreshold.toString(), // DAO setting 3: proposal threshold
          quorumThreshold.toString() // DAO setting 4: quorum threshold
        ]

        // Ensure description is also persisted on-chain via registry for indexers
        const registryForDeploy = (() => {
          const base: Record<string, string> = { ...(daoData.registry || {}) } as any
          if (daoData.description && !base["description"]) {
            base["description"] = String(daoData.description)
          }
          return base
        })()

        const daoCreateObject = {
          name: daoData.name || "",
          symbol: daoData.governanceToken.symbol || "",
          description: daoData.description || "",
          decimals: parseInt(daoData.governanceToken.tokenDecimals) || 18,
          executionDelay: Math.floor(executationDelayinSeconds),
          initialMembers: daoData.members.map((member: any) => member.address),
          initialAmounts: initialAmountsWithSettings,
          keys: Object.keys(registryForDeploy),
          values: Object.values(registryForDeploy).map(v => String(v))
        }

        try {
          wrapper = await wrapperFactory.deployDAOwithToken(daoCreateObject)
        } catch (contractError) {
          console.error("Contract call error:", contractError)
          console.error("Error details:", {
            message: (contractError as any).message,
            code: (contractError as any).code,
            data: (contractError as any).data
          })
          throw contractError
        }
      }

      console.log("Transaction sent:", wrapper.hash)

      const receipt = await wrapper.wait()
      console.log("Transaction confirmed:", receipt)
      try {
        const params = new URLSearchParams()
        if (daoData?.name) params.set("q", String(daoData.name))
        params.set("postDeploy", "dao-created")
        if (wrapper?.hash) params.set("tx", String(wrapper.hash))
        if (network) params.set("network", String(network))
        history.push(`/explorer/daos?${params.toString()}`)
      } catch (_) {
        // Fallback to legacy behavior if URLSearchParams fails for any reason
        history.push(`/explorer/daos?q=${encodeURIComponent(daoData.name || "")}&postDeploy=dao-created`)
      }
      // history.push("/explorer/etherlink/dao/0x287915D27CC4FC967Ca10AA20242d80d99caCe5e/overview")
    } catch (error: any) {
      notify({
        message: `Error deploying DAO: ${
          error?.reason || error?.shortMessage || error?.message || "Unknown error"
        } (wrapper: ${selectedWrapperAddress})`,
        variant: "error"
      })
      setIsDeploying(false)
      return null
    }
    setIsDeploying(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.data, etherlink.signer, contractData])
  const isFinalStep = data.currentStep === STEPS.length - 1

  return {
    ...data,
    isDeploying,
    next: {
      text: isDeploying ? "Deploying..." : isFinalStep ? "Deploy" : "Next",
      disabled: isDeploying,
      handler: () => {
        if (data.data.template === "lite") return history.push("/lite")

        // Validation for 1. DAO Basics
        if (data.currentStep == 1) {
          if (data.data.name === "" || data.data.description === "") {
            return notify({
              message: "Please fill in DAO name and description",
              variant: "error"
            })
          }

          if (data.data.tokenDeploymentMechanism === "new") {
            if (!data.data.governanceToken.symbol || data.data.governanceToken.tokenDecimals < 1) {
              return notify({
                message: "Please fill in token symbol and decimals",
                variant: "error"
              })
            }
          } else if (data.data.tokenDeploymentMechanism === "wrapped") {
            if (!data.data.underlyingTokenAddress || !data.data.wrappedTokenSymbol) {
              return notify({
                message: "Please fill in underlying token address and wrapped token symbol",
                variant: "error"
              })
            }
            if (!/^0x[a-fA-F0-9]{40}$/.test(data.data.underlyingTokenAddress)) {
              return notify({
                message: "Invalid Ethereum address format",
                variant: "error"
              })
            }
            // Clear members when using wrapped token to avoid mismatch with zero initial supply
            data.setFieldValue("members", [])
          }
        }

        // Validation for 2. Proposal & Voting
        if (data.currentStep == 2) {
          const votingData = {
            proposalExpiry: Object.entries(data.data.voting)
              .filter(([key]) => key.startsWith("proposalExpiry"))
              .reduce((acc, [_, value]) => acc + (Number(value) || 0), 0),
            proposalFlush: Object.entries(data.data.voting)
              .filter(([key]) => key.startsWith("proposalFlush"))
              .reduce((acc, [_, value]) => acc + (Number(value) || 0), 0),
            // votingBlock corresponds to Voting Delay; allow zero for immediate start
            votingBlock: Object.entries(data.data.voting)
              .filter(([key]) => key.startsWith("votingBlock"))
              .reduce((acc, [_, value]) => acc + (Number(value) || 0), 0)
          }

          // Require non-zero for voting period and execution delay; allow zero for voting delay
          if (votingData.proposalExpiry === 0 || votingData.proposalFlush === 0) {
            return notify({
              message: "Please add valid values for all time periods",
              variant: "error"
            })
          }
          if (votingData.votingBlock < 0) {
            return notify({ message: "Voting Delay cannot be negative", variant: "error" })
          }
        }

        console.log("Members", data.data.members)

        // Validation for 4. Members
        if (data.currentStep === 4 && data.data.tokenDeploymentMechanism === "new") {
          console.log("Validating members at step 4:", data.data.members)
          const memberErrorExists = data.data.members.some((member: any) => member.error)
          const invalidAddresses = data.data.members.filter(
            (member: any) => !/^0x[a-fA-F0-9]{40}$/.test(member.address)
          )

          if (invalidAddresses.length > 0) {
            console.error("Invalid member addresses:", invalidAddresses)
            return notify({
              message: "Please ensure all member addresses are valid Ethereum addresses",
              variant: "error"
            })
          }

          if (memberErrorExists) {
            return notify({
              message: "Please fix all errors in the members section",
              variant: "error"
            })
          }

          const memberZeroAllocation = data.data.members.some((member: any) => Number(member.amountOfTokens) === 0)

          if (memberZeroAllocation) {
            return notify({
              message: "All members must have a token allocation",
              variant: "error"
            })
          }
        }

        console.log("Registry", data.data.registry)

        // Validation for 5. Registry
        if (data.currentStep === 5) {
          console.log("Validating registry at step 5:", data.data.registry)
          if (!data.data.registry || typeof data.data.registry !== "object") {
            console.error("Registry is not an object:", data.data.registry)
            return notify({
              message: "Registry data is invalid",
              variant: "error"
            })
          }
          const registryErrorExists = Object.entries(data.data.registry).some(([_, value]) => value === "")
          if (registryErrorExists) {
            return notify({
              message: "Please fill in all registry fields",
              variant: "error"
            })
          }
        }

        if (isFinalStep) {
          deployDaoWithWrapper()
        } else {
          const nextStep = data.next.handler()
          if (nextStep) {
            history.push(nextStep)
          }
        }
      }
    },
    prev: {
      text: "Back",
      handler: () => {
        const prevStep = data.prev.handler()
        if (prevStep) {
          history.push(prevStep)
        }
      }
    }
  }
}

export default useEvmDaoCreateStore
